/**
 * Keeping music visuals ON the beat you hear (Gordon 2026-11-04: "make sure
 * the effects really react instantly with the sound … there's a little
 * latency … read the music a little before it plays").
 *
 * The analyser hears the music before the speakers play it: by the output
 * latency of the audio device (+ the context's base latency) — tens of ms on
 * built-in speakers, much more on Bluetooth. The screen shows a frame about
 * one or two frames after it is drawn. So a visual holds each reading back by
 * (output latency − screen time) and lands with the sound.
 */

/** About how long a drawn frame takes to reach the screen. */
export const SCREEN_SEC = 0.03;

/** How long (ms) to hold a reading from `node`'s AudioContext back. 0 when the
 *  browser doesn't say (Safari), or for analysers that aren't real nodes. */
export function visualHoldMs(node: { context?: unknown } | null | undefined): number {
  const ctx = node?.context as { outputLatency?: number; baseLatency?: number } | undefined;
  if (!ctx || (typeof ctx.outputLatency !== "number" && typeof ctx.baseLatency !== "number")) return 0;
  const out = (ctx.outputLatency || 0) + (ctx.baseLatency || 0);
  return Math.max(0, Math.min(350, (out - SCREEN_SEC) * 1000));
}

/** A short memory of readings, so a visual can use the one from `holdMs` ago. */
export class ReadingDelay {
  private readonly size = 32; // > 0.5 s at 60 fps
  private readonly at = new Float64Array(this.size);
  private readonly frames: Float32Array[];
  private head = 0;
  constructor(length: number) {
    this.frames = Array.from({ length: this.size }, () => new Float32Array(length));
  }
  /** Remember `reading` (taken now) and replace it with the newest reading at
   *  least `holdMs` old; leaves it as it is until there is one. */
  hold(reading: { length: number; [i: number]: number }, holdMs: number, now: number, n = reading.length): void {
    if (holdMs < 4) return;
    const slot = this.frames[this.head];
    for (let i = 0; i < n; i++) slot[i] = reading[i];
    this.at[this.head] = now;
    this.head = (this.head + 1) % this.size;
    for (let k = 1; k <= this.size; k++) {
      const j = (this.head - k + this.size) % this.size;
      if (this.at[j] && now - this.at[j] >= holdMs) {
        const old = this.frames[j];
        for (let i = 0; i < n; i++) reading[i] = old[i];
        return;
      }
    }
  }
}
